import { useEffect, useState } from "react";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { useDelayedComponents } from "@hooks/useDelayedComponents";
import { useInjectClientData } from "@hooks/useInjectClientData";
import MyAccountActivePage from "@routes/MyAccount/components/MyAccountActivePage";
import MyAccountPageSetting from "@routes/MyAccount/components/MyAccountPageSetting";
import MyAccountPageSettingPopover from "@routes/MyAccount/components/MyAccountPageSettingPopover";
import MyAccountTabs from "@routes/MyAccount/components/MyAccountTabs";
import {
  MyAccountPageData,
  MyAccountTabsEnum,
} from "@routes/MyAccount/utils/types";

/**
 * My Account Page Component
 */
const MyAccountPageContent = ({
  seoFooterData,
  gaProps,
}: MyAccountPageData & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  const { isShown } = useDelayedComponents(5000);
  const [activeTab, setActiveTab] = useState<MyAccountTabsEnum>(
    MyAccountTabsEnum.myBT
  );

  const { visitorId } = useUserInfo();

  useEffect(() => {
    const getRecentRead = sessionStorage.getItem("account-reading-history");
    if (getRecentRead !== "true") return;

    setActiveTab(MyAccountTabsEnum.recent);
  }, []);

  useEffect(() => {
    if (!isShown) return;
    if (visitorId) return;
    if (typeof window === "undefined") return;

    window.location.href = RouteFactory.home;
  }, [isShown, visitorId]);

  return (
    <div className="w-full" data-testid="my-account-page-component">
      <div data-testid="my-account-page-content" id="my-account-page-component">
        <Ad
          adType="lb1"
          className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
        />

        <Header />

        <Container rootClassName="pb-[10rem]">
          <div className="mb-3 flex items-center justify-between border-b border-gray-850 pb-3 pt-6 lg:mb-0 lg:border-none">
            <h1 className="font-playfair text-5xl font-semibold uppercase text-gray-850 md:text-8xl">
              My Account
            </h1>

            <MyAccountPageSettingPopover />
          </div>

          <MyAccountTabs
            handleTabChange={setActiveTab}
            activeTab={activeTab}
            className="m-0 mb-4 border-0 pt-0 lg:border-b"
          />

          <div className="lg:grid lg:grid-cols-10">
            <div className="border-gray-175 lg:col-span-7 lg:border-r lg:pr-8">
              <MyAccountActivePage activeTab={activeTab} />
            </div>

            <div className="hidden lg:col-span-3 lg:block lg:pl-6 lg:pr-0">
              <MyAccountPageSetting />
            </div>
          </div>
        </Container>
      </div>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
    </div>
  );
};

export default MyAccountPageContent;
